import React, { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import api from "../api";
import TrainingEditor from "./TrainingEditor";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

function TrainingTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // module id, "new", or null
  const load = () => api.get("/admin/training").then((r) => setRows(r.data)).catch((e) => toast({ title: "Couldn't load modules", description: e.response?.data?.detail || "", variant: "destructive" }));
  useEffect(() => { if (!editing) load(); }, [editing]);

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this module? This cannot be undone.")) return;
    await api.delete(`/admin/training/${id}`);
    toast({ title: "Module deleted" });
    load();
  };
  const togglePublish = async (m) => {
    await api.put(`/admin/training/${m.id}`, { ...m, published: !m.published, sort_order: m.order });
    toast({ title: m.published ? "Unpublished" : "Published" });
    load();
  };
  const move = async (index, dir, e) => {
    e.stopPropagation();
    const next = [...rows];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setRows(next);
    await api.post("/admin/training/reorder", { ids: next.map((m) => m.id) });
    load();
  };

  if (editing) return <TrainingEditor moduleId={editing} onBack={() => setEditing(null)} />;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Training modules</h3>
            <p className="text-sm text-slate-500">Click a module to open the editor. Applicants only see published modules, in order.</p>
          </div>
          <Button onClick={() => setEditing("new")} data-testid="new-module-btn" className="btn-primary-gradient gap-1.5 border-0 text-white"><Plus className="h-4 w-4" /> New module</Button>
        </div>
      </Card>
      <div className="space-y-2">
        {rows.map((m, i) => (
          <Card key={m.id} className="cursor-pointer transition-colors hover:border-violet-200" >
            <div className="flex items-center justify-between gap-3" data-testid={`module-row-${m.id}`} onClick={() => setEditing(m.id)}>
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button onClick={(e) => move(i, -1, e)} disabled={i === 0} className="text-slate-400 hover:text-violet-600 disabled:opacity-30" data-testid={`move-up-${m.id}`}><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={(e) => move(i, 1, e)} disabled={i === rows.length - 1} className="text-slate-400 hover:text-violet-600 disabled:opacity-30" data-testid={`move-down-${m.id}`}><ArrowDown className="h-4 w-4" /></button>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-600"><FileText className="h-4 w-4" /></div>
                <div>
                  <div className="font-medium text-slate-900">{i + 1}. {m.title}</div>
                  <div className="text-sm text-slate-500">{m.description || "No description"}{m.knowledge_check?.length ? ` · ${m.knowledge_check.length} question check` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${m.published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-500"}`}>{m.published ? "Published" : "Draft"}</span>
                <label className="flex items-center gap-2 text-xs text-slate-500" onClick={(e) => e.stopPropagation()}><Switch checked={!!m.published} onCheckedChange={() => togglePublish(m)} data-testid={`publish-toggle-${m.id}`} /> Publish</label>
                <button onClick={(e) => { e.stopPropagation(); setEditing(m.id); }} className="rounded-md p-1.5 text-slate-400 hover:bg-violet-50 hover:text-violet-600" data-testid={`edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={(e) => del(m.id, e)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><p className="py-6 text-center text-sm text-slate-400">No modules yet. Click "New module" to build your first lesson.</p></Card>}
      </div>
    </div>
  );
}

function AssessmentTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("mcq");
  const [form, setForm] = useState({ question: "", options: ["", "", "", ""], correct_index: 0, module: "" });
  const [settings, setSettings] = useState({ questions_per_attempt: 0, total_questions: 0 });
  const [perAttempt, setPerAttempt] = useState("");
  const load = () => api.get("/admin/assessment").then((r) => setRows(r.data));
  const loadSettings = () => api.get("/admin/assessment/settings").then((r) => { setSettings(r.data); setPerAttempt(String(r.data.questions_per_attempt || 0)); });
  useEffect(() => { load(); loadSettings(); }, []);

  const switchType = (t) => {
    setType(t);
    setForm((f) => (t === "truefalse"
      ? { question: "", options: ["True", "False"], correct_index: 0, module: f.module }
      : { question: "", options: ["", "", "", ""], correct_index: 0, module: f.module }));
  };

  const add = async () => {
    if (!form.question.trim() || form.options.some((o) => !o.trim())) { toast({ title: "Fill all fields", variant: "destructive" }); return; }
    await api.post("/admin/assessment", { ...form, type });
    switchType(type);
    toast({ title: "Question added" });
    await load(); await loadSettings();
  };
  const del = async (id) => { await api.delete(`/admin/assessment/${id}`); load(); loadSettings(); };

  const saveSettings = async () => {
    const n = Math.max(0, parseInt(perAttempt || "0", 10) || 0);
    try { const r = await api.put("/admin/assessment/settings", { questions_per_attempt: n }); setSettings((s) => ({ ...s, questions_per_attempt: r.data.questions_per_attempt })); toast({ title: "Settings saved" }); }
    catch (e) { toast({ title: "Save failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-bold text-slate-900">Question bank settings</h3>
        <p className="text-sm text-slate-500">Show applicants a random subset of the {settings.total_questions} questions in the bank on each attempt.</p>
        <div className="mt-3 flex items-end gap-3">
          <div className="space-y-1.5">
            <Label>Questions per attempt</Label>
            <Input type="number" min={0} value={perAttempt} onChange={(e) => setPerAttempt(e.target.value)} className="w-40" data-testid="questions-per-attempt-input" placeholder="0 = all" />
          </div>
          <Button onClick={saveSettings} className="btn-primary-gradient border-0 text-white" data-testid="save-assessment-settings-btn">Save</Button>
          <span className="pb-2 text-xs text-slate-400">Enter 0 to use all questions. If fewer questions exist than requested, all are shown.</span>
        </div>
      </Card>
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Add assessment question</h3>
          <div className="flex rounded-lg border border-slate-200 p-0.5" data-testid="question-type-toggle">
            <button onClick={() => switchType("mcq")} className={`rounded-md px-3 py-1 text-xs font-semibold ${type === "mcq" ? "bg-violet-600 text-white" : "text-slate-500"}`} data-testid="type-mcq">Multiple Choice</button>
            <button onClick={() => switchType("truefalse")} className={`rounded-md px-3 py-1 text-xs font-semibold ${type === "truefalse" ? "bg-violet-600 text-white" : "text-slate-500"}`} data-testid="type-truefalse">True / False</button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Question</Label><Textarea rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="assessment-question-input" /></div>
          <div className="space-y-1.5"><Label>Module tag</Label><Input value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} placeholder="e.g. Communication Skills" data-testid="assessment-module-input" /></div>
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-3">
              <input type="radio" checked={form.correct_index === i} onChange={() => setForm({ ...form, correct_index: i })} className="accent-violet-600" data-testid={`assessment-correct-${i}`} />
              {type === "truefalse"
                ? <span className="text-sm font-medium text-slate-700">{opt}</span>
                : <Input placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => { const o = [...form.options]; o[i] = e.target.value; setForm({ ...form, options: o }); }} data-testid={`assessment-option-${i}`} />}
            </div>
          ))}
          <p className="text-xs text-slate-400">Select the radio next to the correct answer. Questions are shown to applicants in a random order.</p>
        </div>
        <Button onClick={add} className="btn-primary-gradient mt-3 gap-1.5 border-0 text-white" data-testid="add-assessment-btn"><Plus className="h-4 w-4" /> Add question</Button>
      </Card>
      <div className="space-y-2">
        {rows.map((q, idx) => (
          <Card key={q.id}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-slate-900">{idx + 1}. {q.question} <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{q.type === "truefalse" ? "T/F" : "MCQ"}</span>{q.module ? <span className="ml-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700" data-testid={`module-tag-${q.id}`}>{q.module}</span> : null}</div>
                <div className="mt-1 text-sm text-slate-500">Answer: {q.options[q.correct_index]}</div>
              </div>
              <button onClick={() => del(q.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function OpportunitiesTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ client_name: "", role_title: "", description: "", location: "Remote (US)", pay_rate: "", requirements: "" });
  const load = () => api.get("/admin/opportunities").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.client_name.trim() || !form.role_title.trim()) { toast({ title: "Client and role required", variant: "destructive" }); return; }
    await api.post("/admin/opportunities", {
      ...form, status: "open",
      pay_rate: parseFloat(form.pay_rate) || 0,
      requirements: form.requirements.split(",").map((s) => s.trim()).filter(Boolean),
    });
    setForm({ client_name: "", role_title: "", description: "", location: "Remote (US)", pay_rate: "", requirements: "" });
    toast({ title: "Opportunity added" });
    await load();
  };
  const del = async (id) => { await api.delete(`/admin/opportunities/${id}`); load(); };
  const toggleStatus = async (o) => {
    const status = o.status === "closed" ? "open" : "closed";
    await api.put(`/admin/opportunities/${o.id}`, { ...o, status, requirements: o.requirements || [] });
    toast({ title: `Opportunity ${status}` });
    load();
  };
  return (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-bold text-slate-900">Add opportunity</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Client name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Role title</Label><Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Pay rate ($/hr)</Label><Input type="number" value={form.pay_rate} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Requirements (comma separated)</Label><Input value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} /></div>
        </div>
        <Button onClick={add} className="btn-primary-gradient mt-3 gap-1.5 border-0 text-white"><Plus className="h-4 w-4" /> Add opportunity</Button>
      </Card>
      <div className="space-y-2">
        {rows.map((o) => (
          <Card key={o.id}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-slate-900">{o.role_title} · {o.client_name}</div>
                <div className="text-sm text-slate-500">{o.location} — ${o.pay_rate}/hr · <span className={o.status === "closed" ? "text-rose-500" : "text-emerald-600"}>{o.status || "open"}</span></div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleStatus(o)}>{o.status === "closed" ? "Open" : "Close"}</Button>
                <button onClick={() => del(o.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function AdminContent() {
  return (
    <Tabs defaultValue="training" className="space-y-4">
      <TabsList>
        <TabsTrigger value="training">Training</TabsTrigger>
        <TabsTrigger value="assessment">Assessment</TabsTrigger>
        <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
      </TabsList>
      <TabsContent value="training"><TrainingTab /></TabsContent>
      <TabsContent value="assessment"><AssessmentTab /></TabsContent>
      <TabsContent value="opportunities"><OpportunitiesTab /></TabsContent>
    </Tabs>
  );
}
